/**
 * Laravel route extraction from route files.
 * Parses Route::get/post/resource/apiResource calls using regex on PHP source.
 *
 * Supports:
 * - Class array syntax: Route::get('/path', [Controller::class, 'method'])  (L8+)
 * - String syntax: Route::get('/path', 'Controller@method')                (L6-8)
 * - Invokable: Route::get('/path', Controller::class)                      (L8+)
 * - Route::resource / Route::apiResource
 * - Route::controller(X::class)->group(...)                                (L9+)
 * - Route::namespace('Admin')->group(...)                                  (L6-8)
 * - Route::middleware(...)->group(...)
 * - Route::prefix(...)->... chains
 */
import type { RawRoute } from '../../../../../plugin-api/types.js';

/** HTTP methods supported by Laravel routing. */
const HTTP_METHODS = ['get', 'post', 'put', 'patch', 'delete', 'options', 'any'];

/** Resource route methods generated by Route::resource(). */
const RESOURCE_METHODS = [
  { method: 'GET', suffix: '', action: 'index' },
  { method: 'GET', suffix: '/create', action: 'create' },
  { method: 'POST', suffix: '', action: 'store' },
  { method: 'GET', suffix: '/{id}', action: 'show' },
  { method: 'GET', suffix: '/{id}/edit', action: 'edit' },
  { method: 'PUT', suffix: '/{id}', action: 'update' },
  { method: 'DELETE', suffix: '/{id}', action: 'destroy' },
];

/** API resource methods (no create/edit views). */
const API_RESOURCE_METHODS = RESOURCE_METHODS.filter(
  (m) => m.action !== 'create' && m.action !== 'edit',
);

interface RouteExtractionResult {
  routes: RawRoute[];
  warnings: string[];
}

/**
 * Context accumulated from group wrappers (namespace, prefix, middleware, controller).
 */
interface GroupContext {
  namespace?: string;
  prefix?: string;
  middleware?: string[];
  controller?: string;
}

/**
 * Extract routes from a PHP route file source.
 * Handles all Laravel route definition patterns from L6 to L13.
 */
export function extractRoutes(source: string, filePath: string): RouteExtractionResult {
  void filePath; // served-path `/api` prefixing for api.php is applied in the topology layer
  const routes: RawRoute[] = [];
  const useMap = buildUseMap(source);
  // Single recursive, group-aware pass. Route groups — chained
  // (`Route::prefix('p')->middleware([...])->group(fn)`) or array
  // (`Route::group(['prefix'=>'p',...], fn)`) — carry prefix / middleware /
  // namespace / controller context down to the routes they wrap, with arbitrary
  // nesting. Group bodies are masked out of each scope so a route is processed
  // exactly once with the correct accumulated prefix; dedupeRoutes then merges
  // the intentional overlap between the leaf extractors.
  walkScope(source, useMap, routes, {});
  return { routes: dedupeRoutes(routes), warnings: [] };
}

/**
 * Extract every route inside a source scope under an accumulated group context,
 * after recursing into (and masking out) any nested `->group()` blocks.
 */
function walkScope(
  scope: string,
  useMap: Map<string, string>,
  routes: RawRoute[],
  ctx: GroupContext,
): void {
  const masked = maskAndRecurseGroups(scope, useMap, routes, ctx);
  // Controller groups: inner routes use the short `Route::get('/x', 'action')` form.
  if (ctx.controller) extractControllerGroupRoutes(masked, ctx.controller, ctx, routes);
  extractSimpleRoutes(masked, useMap, routes, ctx);
  extractResourceRoutes(masked, useMap, routes, ctx);
  extractInlineRoutes(masked, useMap, routes, ctx);
}

/**
 * Extract routes where the HTTP method is reached through a chain
 * (`Route::middleware([...])->prefix('x')->get('/uri', target)`) or where the
 * handler is a closure (`Route::get('/uri', function () { ... })`). Additive and
 * deduped against the controller-centric extractors.
 */
function extractInlineRoutes(
  source: string,
  useMap: Map<string, string>,
  routes: RawRoute[],
  ctx: GroupContext,
): void {
  const methodPattern = HTTP_METHODS.join('|');
  // Optional leading chain of middleware/prefix/name/domain/etc., then a method.
  const regex = new RegExp(
    `Route::((?:(?:middleware|prefix|name|domain|scopeBindings|withoutMiddleware|as|where|whereNumber|whereUuid)\\s*\\([^)]*\\)\\s*->\\s*)*)(${methodPattern})\\s*\\(\\s*['"]([^'"]+)['"]([^;]*);`,
    'g',
  );

  let match: RegExpExecArray | null;
  while ((match = regex.exec(source)) !== null) {
    const lead = match[1] ?? '';
    const method = match[2].toUpperCase();
    let uri = match[3];
    const rest = match[4] ?? '';

    // Inline prefix in the leading chain (rare but valid), composed onto ctx.
    const inlinePrefix = lead.match(/prefix\s*\(\s*['"]([^'"]+)['"]/)?.[1];
    let prefix = ctx.prefix;
    if (inlinePrefix) prefix = prefix ? `${prefix}/${inlinePrefix}` : inlinePrefix;
    uri = applyPrefix(uri, prefix);

    const controllerSymbolId = resolveTargetController(rest, useMap, ctx);

    const chainMiddleware = [...parseLeadMiddleware(lead), ...extractChainedMiddleware(rest)];
    const middleware = mergeMiddleware(ctx.middleware, chainMiddleware);
    const name = parseLeadName(lead) ?? extractChainedName(rest) ?? undefined;

    routes.push({
      method,
      uri,
      name,
      controllerSymbolId,
      middleware: middleware.length > 0 ? middleware : undefined,
    });
  }
}

/** Resolve the controller target of a route's second argument (the text after the URI). */
function resolveTargetController(
  rest: string,
  useMap: Map<string, string>,
  ctx: GroupContext,
): string | undefined {
  // [Controller::class, 'method']
  let m = rest.match(/^\s*,\s*\[\s*([\w\\]+::class)\s*,\s*['"]([^'"]+)['"]\s*\]/);
  if (m) return `${resolveClass(m[1], useMap, ctx.namespace)}::${m[2]}`;
  // 'Controller@method'
  m = rest.match(/^\s*,\s*['"]([^'"]+@[^'"]+)['"]/);
  if (m) {
    const [c, a] = m[1].split('@');
    return `${resolveClass(c, useMap, ctx.namespace)}::${a}`;
  }
  // Invokable: Controller::class
  m = rest.match(/^\s*,\s*([\w\\]+::class)\s*\)/);
  if (m) return `${resolveClass(m[1], useMap, ctx.namespace)}::__invoke`;
  // Short action string inside a Route::controller() group
  m = rest.match(/^\s*,\s*['"]([^'"@/]+)['"]/);
  if (m && ctx.controller) return `${ctx.controller}::${m[1]}`;
  // Closure or unresolved — endpoint still registered, no controller symbol.
  return undefined;
}

/** Extract the first middleware(...) call from a leading chain string. */
function parseLeadMiddleware(lead: string): string[] {
  const m = lead.match(/middleware\s*\(\s*(['"[][^)]*)\)/);
  return m ? parseMiddlewareArg(m[1]) : [];
}

/** Extract a name(...) / as(...) call from a leading chain string. */
function parseLeadName(lead: string): string | null {
  const m = lead.match(/(?:name|as)\s*\(\s*['"]([^'"]+)['"]\s*\)/);
  return m ? m[1] : null;
}

/** Dedupe routes by (method, uri), merging controller + middleware + name. */
function dedupeRoutes(routes: RawRoute[]): RawRoute[] {
  const byKey = new Map<string, RawRoute>();
  for (const r of routes) {
    const key = `${r.method} ${r.uri}`;
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, { ...r });
      continue;
    }
    if (!existing.controllerSymbolId && r.controllerSymbolId) {
      existing.controllerSymbolId = r.controllerSymbolId;
    }
    if (!existing.name && r.name) existing.name = r.name;
    const merged = mergeMiddleware(existing.middleware, r.middleware);
    existing.middleware = merged.length > 0 ? merged : undefined;
  }
  return [...byKey.values()];
}

/** Build a map of short class name -> FQN from `use` statements. */
function buildUseMap(source: string): Map<string, string> {
  const map = new Map<string, string>();
  const useRegex = /use\s+([\w\\]+?)(?:\s+as\s+(\w+))?;/g;
  let match: RegExpExecArray | null;
  while ((match = useRegex.exec(source)) !== null) {
    const fqn = match[1];
    const alias = match[2] ?? fqn.split('\\').pop()!;
    map.set(alias, fqn);
  }
  return map;
}

/**
 * Resolve a class reference to FQN.
 * Handles:
 * - UserController::class (short name, lookup in useMap)
 * - \App\Http\Controllers\PostController::class (FQN)
 * - 'UserController@index' string syntax → namespace-aware
 * - Plain string 'UserController' for invokable
 */
function resolveClass(ref: string, useMap: Map<string, string>, namespace?: string): string {
  // ::class syntax: short name
  const shortMatch = ref.match(/^(\w+)::class$/);
  if (shortMatch) {
    return useMap.get(shortMatch[1]) ?? shortMatch[1];
  }

  // ::class syntax: FQN with backslashes
  const fqnMatch = ref.match(/^\\?([\w\\]+)::class$/);
  if (fqnMatch) return fqnMatch[1];

  // Plain class name (no :: no @) — e.g. from string syntax or invokable
  if (!ref.includes('::') && !ref.includes('@')) {
    // Check if it contains backslashes (already namespaced)
    if (ref.includes('\\')) {
      return ref.replace(/^\\/, '');
    }
    // Apply namespace context if present
    if (namespace) {
      return `${namespace}\\${ref}`;
    }
    return useMap.get(ref) ?? ref;
  }

  return ref;
}

// Chained group header: `Route::prefix('p')->middleware([...])->...->group(fn|arrow {`.
// Each chain call's args use `[^)]*`, which tolerates a middleware ARRAY literal
// (`middleware(['a','b'])`) since arrays contain no `)`.
const GROUP_CHAIN_RE =
  /Route::((?:(?:namespace|prefix|middleware|controller|name|domain|scopeBindings|withoutMiddleware|as|where|whereNumber|whereUuid)\s*\([^)]*\)\s*->\s*)+)group\s*\(\s*(?:function\s*\([^)]*\)\s*(?:use\s*\([^)]*\)\s*)?|\([^)]*\)\s*=>\s*)\{/g;

// Array-syntax group: `Route::group(['prefix'=>'p','middleware'=>['a','b']], fn {`.
// The array capture allows one level of nested `[...]` (the middleware array).
const GROUP_ARRAY_RE =
  /Route::group\s*\(\s*(\[(?:[^[\]]|\[[^\]]*\])*\])\s*,\s*(?:function\s*\([^)]*\)\s*(?:use\s*\([^)]*\)\s*)?|\([^)]*\)\s*=>\s*)\{/g;

interface GroupHeader {
  start: number;
  bodyStart: number; // index just after the opening `{`
  kind: 'chain' | 'array';
  spec: string; // chain string (chain) or the `[...]` array-args literal (array)
}

/** Find the earliest `->group(... {` header (chain or array) in `scope`, or null. */
function findNextGroupHeader(scope: string): GroupHeader | null {
  let best: GroupHeader | null = null;
  for (const [re, kind] of [
    [GROUP_CHAIN_RE, 'chain'],
    [GROUP_ARRAY_RE, 'array'],
  ] as const) {
    re.lastIndex = 0;
    const m = re.exec(scope);
    if (m && (best === null || m.index < best.start)) {
      best = { start: m.index, bodyStart: m.index + m[0].length, kind, spec: m[1] ?? '' };
    }
  }
  return best;
}

/** Replace [start,end) with spaces, preserving length/offsets. */
function blankSpan(s: string, start: number, end: number): string {
  return s.slice(0, start) + ' '.repeat(end - start) + s.slice(end);
}

/**
 * Process every `->group()` block in `scope`: recurse into its body with the
 * merged context, then blank the whole group statement out so the leaf
 * extractors don't re-process those routes (which would lose the group prefix).
 * Returns the masked scope.
 */
function maskAndRecurseGroups(
  scope: string,
  useMap: Map<string, string>,
  routes: RawRoute[],
  ctx: GroupContext,
): string {
  let working = scope;
  for (let guard = 0; guard < 10_000; guard++) {
    const header = findNextGroupHeader(working);
    if (!header) break;

    const body = extractGroupBody(working, header.bodyStart);
    if (body === null) {
      // Unbalanced braces — blank just the header so we still make progress.
      working = blankSpan(working, header.start, header.bodyStart);
      continue;
    }
    const spanEnd = header.bodyStart + body.length + 1; // past the matching `}`
    walkScope(body, useMap, routes, mergeGroupContext(ctx, header, useMap));
    working = blankSpan(working, header.start, spanEnd);
  }
  return working;
}

/** Merge a group header's prefix/middleware/namespace/controller onto the parent context. */
function mergeGroupContext(
  parent: GroupContext,
  header: GroupHeader,
  useMap: Map<string, string>,
): GroupContext {
  const ctx: GroupContext = { ...parent };
  let prefix: string | undefined;
  let namespace: string | undefined;
  let controller: string | undefined;
  let middleware: string[] = [];

  if (header.kind === 'array') {
    const a = header.spec;
    prefix = a.match(/['"]prefix['"]\s*=>\s*['"]([^'"]+)['"]/)?.[1];
    namespace = a.match(/['"]namespace['"]\s*=>\s*['"]([^'"]+)['"]/)?.[1];
    controller = a.match(/['"]controller['"]\s*=>\s*([\w\\]+::class)/)?.[1];
    const mw = a.match(/['"]middleware['"]\s*=>\s*(\[[^\]]*\]|['"][^'"]+['"])/);
    if (mw) middleware = parseMiddlewareArg(mw[1]);
  } else {
    const chain = header.spec;
    prefix = chain.match(/prefix\s*\(\s*['"]([^'"]+)['"]\s*\)/)?.[1];
    namespace = chain.match(/namespace\s*\(\s*['"]([^'"]+)['"]\s*\)/)?.[1];
    controller = chain.match(/controller\s*\(\s*([\w\\]+::class)\s*\)/)?.[1];
    middleware = parseLeadMiddleware(chain);
  }

  if (prefix) {
    const p = prefix.replace(/^\/+|\/+$/g, '');
    if (p) ctx.prefix = parent.prefix ? `${parent.prefix}/${p}` : p;
  }
  if (namespace) ctx.namespace = parent.namespace ? `${parent.namespace}\\${namespace}` : namespace;
  if (middleware.length > 0) ctx.middleware = mergeMiddleware(parent.middleware, middleware);
  if (controller) ctx.controller = resolveClass(controller, useMap, ctx.namespace);

  return ctx;
}

/**
 * Extract routes from inside a Route::controller() group.
 * These routes only specify the action name as a string: Route::get('/path', 'action')
 */
function extractControllerGroupRoutes(
  body: string,
  controllerFqn: string,
  ctx: GroupContext,
  routes: RawRoute[],
): void {
  const methodPattern = HTTP_METHODS.join('|');
  const regex = new RegExp(
    `Route::(${methodPattern})\\s*\\(\\s*['"]([^'"]+)['"]\\s*,\\s*['"]([^'"]+)['"]\\s*\\)([^;]*);`,
    'g',
  );

  let match: RegExpExecArray | null;
  while ((match = regex.exec(body)) !== null) {
    const method = match[1].toUpperCase();
    const uri = applyPrefix(match[2], ctx.prefix);
    const action = match[3];
    const chain = match[4];

    const name = extractChainedName(chain);
    const chainMiddleware = extractChainedMiddleware(chain);
    const middleware = mergeMiddleware(ctx.middleware, chainMiddleware);

    routes.push({
      method,
      uri,
      name: name ?? undefined,
      controllerSymbolId: `${controllerFqn}::${action}`,
      middleware: middleware.length > 0 ? middleware : undefined,
    });
  }
}

/**
 * Extract Route::get/post/put/patch/delete/any calls.
 * Supports:
 * - [Controller::class, 'method'] array syntax
 * - 'Controller@method' string syntax
 * - Controller::class invokable syntax
 */
function extractSimpleRoutes(
  source: string,
  useMap: Map<string, string>,
  routes: RawRoute[],
  ctx: GroupContext,
): void {
  const methodPattern = HTTP_METHODS.join('|');

  // 1. Array syntax: Route::get('/path', [Controller::class, 'method'])
  const arrayRegex = new RegExp(
    `Route::(${methodPattern})\\s*\\(\\s*['"]([^'"]+)['"]\\s*,\\s*\\[\\s*([\\w\\\\]+::class)\\s*,\\s*['"]([^'"]+)['"]\\s*\\]\\s*\\)([^;]*);`,
    'g',
  );

  let match: RegExpExecArray | null;
  while ((match = arrayRegex.exec(source)) !== null) {
    const method = match[1].toUpperCase();
    const uri = applyPrefix(match[2], ctx.prefix);
    const controllerFqn = resolveClass(match[3], useMap, ctx.namespace);
    const action = match[4];
    const chain = match[5];

    const name = extractChainedName(chain);
    const chainMiddleware = extractChainedMiddleware(chain);
    const middleware = mergeMiddleware(ctx.middleware, chainMiddleware);

    routes.push({
      method,
      uri,
      name: name ?? undefined,
      controllerSymbolId: `${controllerFqn}::${action}`,
      middleware: middleware.length > 0 ? middleware : undefined,
    });
  }

  // 2. String syntax: Route::get('/path', 'Controller@method') — Laravel 6-8
  const stringRegex = new RegExp(
    `Route::(${methodPattern})\\s*\\(\\s*['"]([^'"]+)['"]\\s*,\\s*'([^']+@[^']+)'\\s*\\)([^;]*);`,
    'g',
  );
  while ((match = stringRegex.exec(source)) !== null) {
    const method = match[1].toUpperCase();
    const uri = applyPrefix(match[2], ctx.prefix);
    const controllerAction = match[3]; // e.g. 'UserController@index' or 'Admin\UserController@index'
    const chain = match[4];

    const [controllerName, action] = controllerAction.split('@');
    const controllerFqn = resolveClass(controllerName, useMap, ctx.namespace);

    const name = extractChainedName(chain);
    const chainMiddleware = extractChainedMiddleware(chain);
    const middleware = mergeMiddleware(ctx.middleware, chainMiddleware);

    routes.push({
      method,
      uri,
      name: name ?? undefined,
      controllerSymbolId: `${controllerFqn}::${action}`,
      middleware: middleware.length > 0 ? middleware : undefined,
    });
  }

  // 3. Also match string syntax with double quotes
  const stringRegexDQ = new RegExp(
    `Route::(${methodPattern})\\s*\\(\\s*['"]([^'"]+)['"]\\s*,\\s*"([^"]+@[^"]+)"\\s*\\)([^;]*);`,
    'g',
  );
  while ((match = stringRegexDQ.exec(source)) !== null) {
    const method = match[1].toUpperCase();
    const uri = applyPrefix(match[2], ctx.prefix);
    const controllerAction = match[3];
    const chain = match[4];

    const [controllerName, action] = controllerAction.split('@');
    const controllerFqn = resolveClass(controllerName, useMap, ctx.namespace);

    const name = extractChainedName(chain);
    const chainMiddleware = extractChainedMiddleware(chain);
    const middleware = mergeMiddleware(ctx.middleware, chainMiddleware);

    routes.push({
      method,
      uri,
      name: name ?? undefined,
      controllerSymbolId: `${controllerFqn}::${action}`,
      middleware: middleware.length > 0 ? middleware : undefined,
    });
  }

  // 4. Invokable controller: Route::get('/path', Controller::class)
  const invokableRegex = new RegExp(
    `Route::(${methodPattern})\\s*\\(\\s*['"]([^'"]+)['"]\\s*,\\s*([\\w\\\\]+::class)\\s*\\)([^;]*);`,
    'g',
  );
  while ((match = invokableRegex.exec(source)) !== null) {
    const method = match[1].toUpperCase();
    const uri = applyPrefix(match[2], ctx.prefix);
    const controllerFqn = resolveClass(match[3], useMap, ctx.namespace);
    const chain = match[4];

    // Check this isn't already captured by array syntax (which would have a comma + bracket after class)
    // The invokable regex only matches when ::class is followed by ) directly
    const name = extractChainedName(chain);
    const chainMiddleware = extractChainedMiddleware(chain);
    const middleware = mergeMiddleware(ctx.middleware, chainMiddleware);

    routes.push({
      method,
      uri,
      name: name ?? undefined,
      controllerSymbolId: `${controllerFqn}::__invoke`,
      middleware: middleware.length > 0 ? middleware : undefined,
    });
  }
}

/** Extract Route::resource and Route::apiResource calls. */
function extractResourceRoutes(
  source: string,
  useMap: Map<string, string>,
  routes: RawRoute[],
  ctx: GroupContext,
): void {
  // Class-based: Route::resource('posts', PostController::class)
  const classRegex =
    /Route::(resource|apiResource)\s*\(\s*['"]([^'"]+)['"]\s*,\s*([\w\\]+::class)\s*\)([^;]*);/g;

  let match: RegExpExecArray | null;
  while ((match = classRegex.exec(source)) !== null) {
    const type = match[1];
    const prefix = match[2];
    const controllerFqn = resolveClass(match[3], useMap, ctx.namespace);
    const chain = match[4];
    const chainMiddleware = extractChainedMiddleware(chain);
    const middleware = mergeMiddleware(ctx.middleware, chainMiddleware);

    const methods = type === 'apiResource' ? API_RESOURCE_METHODS : RESOURCE_METHODS;

    for (const rm of methods) {
      const uri = applyPrefix(`/${prefix}${rm.suffix}`, ctx.prefix);
      routes.push({
        method: rm.method,
        uri,
        name: `${prefix}.${rm.action}`,
        controllerSymbolId: `${controllerFqn}::${rm.action}`,
        middleware: middleware.length > 0 ? middleware : undefined,
      });
    }
  }

  // String-based: Route::resource('posts', 'PostController') — Laravel 6-8
  const stringRegex =
    /Route::(resource|apiResource)\s*\(\s*['"]([^'"]+)['"]\s*,\s*['"]([^'"]+)['"]\s*\)([^;]*);/g;

  while ((match = stringRegex.exec(source)) !== null) {
    const type = match[1];
    const prefix = match[2];
    const controllerName = match[3];
    const chain = match[4];

    // Skip if this looks like a class reference (already handled above)
    if (controllerName.includes('::')) continue;

    const controllerFqn = resolveClass(controllerName, useMap, ctx.namespace);
    const chainMiddleware = extractChainedMiddleware(chain);
    const middleware = mergeMiddleware(ctx.middleware, chainMiddleware);

    const methods = type === 'apiResource' ? API_RESOURCE_METHODS : RESOURCE_METHODS;

    for (const rm of methods) {
      const uri = applyPrefix(`/${prefix}${rm.suffix}`, ctx.prefix);
      routes.push({
        method: rm.method,
        uri,
        name: `${prefix}.${rm.action}`,
        controllerSymbolId: `${controllerFqn}::${rm.action}`,
        middleware: middleware.length > 0 ? middleware : undefined,
      });
    }
  }
}

/** Extract ->name('...') from method chain. */
function extractChainedName(chain: string): string | null {
  const match = chain.match(/->name\s*\(\s*['"]([^'"]+)['"]\s*\)/);
  return match ? match[1] : null;
}

/** Extract ->middleware('...') or ->middleware(['...', '...']) from method chain. */
function extractChainedMiddleware(chain: string): string[] {
  const match = chain.match(/->middleware\s*\(\s*(['"[].*?)\s*\)/);
  if (!match) return [];

  const arg = match[1];
  // Array form: ['auth', 'verified']
  if (arg.startsWith('[')) {
    const items: string[] = [];
    const regex = /['"]([^'"]+)['"]/g;
    let m: RegExpExecArray | null;
    while ((m = regex.exec(arg)) !== null) {
      items.push(m[1]);
    }
    return items;
  }

  // Single string: 'auth'
  const single = arg.match(/['"]([^'"]+)['"]/);
  return single ? [single[1]] : [];
}

/** Parse a middleware argument which can be 'name', "name", or ['a', 'b']. */
function parseMiddlewareArg(arg: string): string[] {
  const trimmed = arg.trim();
  if (trimmed.startsWith('[')) {
    const items: string[] = [];
    const regex = /['"]([^'"]+)['"]/g;
    let m: RegExpExecArray | null;
    while ((m = regex.exec(trimmed)) !== null) {
      items.push(m[1]);
    }
    return items;
  }
  const single = trimmed.match(/['"]([^'"]+)['"]/);
  return single ? [single[1]] : [];
}

/**
 * Extract the body of a group closure starting at a given position.
 * Counts braces to find the matching closing brace.
 */
function extractGroupBody(source: string, startIndex: number): string | null {
  let depth = 1;
  let i = startIndex;
  while (i < source.length && depth > 0) {
    if (source[i] === '{') depth++;
    else if (source[i] === '}') depth--;
    i++;
  }
  if (depth !== 0) return null;
  return source.substring(startIndex, i - 1);
}

/** Prepend prefix to a URI. */
function applyPrefix(uri: string, prefix?: string): string {
  if (!prefix) return uri;
  const normalizedPrefix = prefix.startsWith('/') ? prefix : `/${prefix}`;
  const normalizedUri = uri.startsWith('/') ? uri : `/${uri}`;
  return `${normalizedPrefix}${normalizedUri}`;
}

/** Merge parent middleware with local middleware, parent first. */
function mergeMiddleware(parent?: string[], local?: string[]): string[] {
  const result = [...(parent ?? [])];
  for (const m of local ?? []) {
    if (!result.includes(m)) {
      result.push(m);
    }
  }
  return result;
}
